import {Payments, Registration} from "./Types.ts";
import {sum, toFixed} from "./Utils.ts";

const YEAR = new Date().getFullYear();

export type PricingLineItem = { quantity: number, price: number}
export type PricingInfo = {
    total: number,
    P0: PricingLineItem,
    P1: PricingLineItem,
    P2: PricingLineItem,
    P3: PricingLineItem,
    P4: PricingLineItem,
    P5: PricingLineItem,
    usedDate: number,
    usedDateRationale: string,
    usedDatePeriod: string,
    periodEnds?: number,
}

export function calculateCost(registration: Registration, paymentsForDate: Payments): PricingInfo {
    // date of first payment is used to calculate late pricing vs not.
    const ret: PricingInfo = {
        total: 0,
        P0: {quantity: 0, price: 0},
        P1: {quantity: 0, price: 0},
        P2: {quantity: 0, price: 0},
        P3: {quantity: 0, price: 0},
        P4: {quantity: 0, price: 0},
        P5: {quantity: 0, price: 0},
        usedDate: 0,
        usedDateRationale: '',
        usedDatePeriod: '',
    };
    if (!paymentsForDate?.payments?.length) {
        ret.usedDate = Date.now();
        ret.usedDateRationale = 'No payments, using current date.';
    } else {
        ret.usedDate = Math.min(...(paymentsForDate?.payments ?? []).map(p => p.date));
        ret.usedDateRationale = 'Using date of first payment.';
    }
    let period = 2; // If before March 22, period = 0, if before April 6th, period = 1, if after April 6th, period = 2.
    const first_period = new Date(`March 22, ${YEAR}, 23:59:59`);
    const second_period = new Date(`April 6, ${YEAR}, 23:59:59`);
    // console.log(new Date(ret.usedDate), first_period, second_period, ret.usedDate < first_period.getTime(), ret.usedDate < second_period.getTime())

    if (ret.usedDate < second_period.getTime()) {
        ret.periodEnds = second_period.getTime();
        period = 1;
    }
    if (ret.usedDate < first_period.getTime()) {
        ret.periodEnds = first_period.getTime();
        period = 0;
    }
    ret.usedDatePeriod = [`Before ${first_period.getMonth() + 1}/${first_period.getDate()}`, `${first_period.getMonth() + 1}/${first_period.getDate()} - ${second_period.getMonth() + 1}/${second_period.getDate()}`, `After ${second_period.getMonth() + 1}/${second_period.getDate()}`].at(period);
    ret.P0.price = 0; // 3 and under
    ret.P1.price = [0, 75, 125][period]; // children registered for RE
    ret.P2.price = [75, 100, 125][period]; // children not registered for RE

    // ret.P3   Regular Adult
    switch (registration.abilityToPay) {
        case 'I can pay more than my fair share':
            ret.P3.price = [175, 175, 225][period];
            break;
        case 'I can pay my fair share':
            ret.P3.price = [175, 175, 225][period];
            break;
        case 'I can pay a reduced rate':
            ret.P3.price = [100, 175, 225][period];
            break;
        case 'Please waive my registration fees':
            ret.P3.price = [0, 0, 0][period];
            break;
        case 'Specialized Fees':
            ret.P3.price = [0, 0, 0][period];
    }
    ret.P4.price = [60, 75, 100][period]; // Day tripper adult
    ret.P5.price = [0, 40, 50][period]; // Day tripper child (regardless of RE)

    const day_tripper = ['day-tripper'].includes(registration.sleepingArrangementChoice);

    for (const attendee of (registration.attendees ?? [])) {
        if (attendee.ageOnApril24  == '18 or over') { // Regular adult
            if (day_tripper) ret.P4.quantity++;
            else ret.P3.quantity++;
            continue;
        }
        const age = parseInt(attendee.ageOnApril24);
        if (age <= 3) {
            ret.P0.quantity++;
            continue;
        }
        if (day_tripper) {
            ret.P5.quantity++;
            continue;
        }

        if (attendee.registeredForRE) {
            ret.P1.quantity++;
        } else {
            ret.P2.quantity++;
        }
    }
    ret.total = ret.P0.price * ret.P0.quantity + ret.P1.price * ret.P1.quantity + ret.P2.price * ret.P2.quantity + ret.P3.price * ret.P3.quantity + ret.P4.price * ret.P4.quantity + ret.P5.price * ret.P5.quantity;
    const donation = Math.max(registration.donationAmount, 0);
    ret.total += registration.abilityToPay === 'I can pay more than my fair share' ? donation : 0;
    return ret;
}

export function renderPricingInfo(registration: Registration, pricingInfo: PricingInfo, payments: Payments, usePicoCSSTooltips: boolean = false): string {
    const donation = (registration.abilityToPay === 'I can pay more than my fair share' && ((registration.donationAmount ?? 0) !== 0)) ? `
        <tr>
            <td colspan="3">Donation Amount - <b>thank you for your donation! 🙌</b></td>
            <td>$${toFixed(registration.donationAmount ?? 0, 0)}</td>
        </tr>` : '';
    const totalPaid = sum(...(payments?.payments ?? []).map(e => e.amount));
    const paymentsMade = payments?.payments?.filter(payment => payment.amount !== 0).map(payment => `
            <tr>
                <td colspan="3">Payment on ${new Date(payment.date).toLocaleString()}</td>
                <td>${payment.amount > 0 ? '-' : ''}$${toFixed(payment.amount, 0)}</td>
            </tr>
        `
    ).join('')
    return `
        <div style="overflow-x: scroll;">
            <table>
                <thead>
                <tr>
                    <th>Category</th>
                    <th>Number of People</th>
                    <th>Fee Each Person <em ${usePicoCSSTooltips ? 'data-tooltip' : 'title'}="${pricingInfo.usedDateRationale}">(${pricingInfo.usedDatePeriod} pricing)</em></th>
                    <th>Subtotal</th>
                </tr>
                </thead>
                <tbody>
                <tr>
                    <td>Children 3 and under</td>
                    <td>${toFixed(pricingInfo.P0.quantity, 0)}</td>
                    <td>$${toFixed(pricingInfo.P0.price, 0)}</td>
                    <td>$${toFixed(pricingInfo.P0.price * pricingInfo.P0.quantity, 0)}</td>
                </tr>
                <tr>
                    <td>Youth Registered for RE</td>
                    <td>${toFixed(pricingInfo.P1.quantity, 0)}</td>
                    <td>$${toFixed(pricingInfo.P1.price, 0)}</td>
                    <td>$${toFixed(pricingInfo.P1.price * pricingInfo.P1.quantity, 0)}</td>
                </tr>
                <tr>
                    <td>Youth Not Registered for RE</td>
                    <td>${toFixed(pricingInfo.P2.quantity, 0)}</td>
                    <td>$${toFixed(pricingInfo.P2.price, 0)}</td>
                    <td>$${toFixed(pricingInfo.P2.price * pricingInfo.P2.quantity, 0)}</td>
                </tr>
                <tr>
                    <td>Adult</td>
                    <td>${toFixed(pricingInfo.P3.quantity, 0)}</td>
                    <td>$${toFixed(pricingInfo.P3.price, 0)}</td>
                    <td>$${toFixed(pricingInfo.P3.price * pricingInfo.P3.quantity, 0)}</td>
                </tr>
                <tr>
                    <td>Adult Saturday Only</td>
                    <td>${toFixed(pricingInfo.P4.quantity, 0)}</td>
                    <td>$${toFixed(pricingInfo.P4.price, 0)}</td>
                    <td>$${toFixed(pricingInfo.P4.price * pricingInfo.P4.quantity, 0)}</td>
                </tr>
                <tr>
                    <td>Child Saturday Only</td>
                    <td>${toFixed(pricingInfo.P5.quantity, 0)}</td>
                    <td>$${toFixed(pricingInfo.P5.price, 0)}</td>
                    <td>$${toFixed(pricingInfo.P5.price * pricingInfo.P5.quantity, 0)}</td>
                </tr>
                ${donation}
                <tr>
                    <td>Total Registration Cost</td>
                    <td></td>
                    <td></td>
                    <td>$${toFixed(pricingInfo.total, 0)}</td>
                </tr>
                ${paymentsMade}
                <tr>
                    <td>Total Amount Due</td>
                    <td></td>
                    <td></td>
                    <td>$${toFixed(pricingInfo.total - totalPaid, 0)}</td>
                </tr>
                </tbody>
            </table>
        </div>`
}